import {
  getLocationBySlug,
  resolveLocationByPathname,
  type PlatformLocation,
} from "./restaurant-platform";

export const publicLocationCookieName = "tc_public_location_id";
export const publicLocationHeaderName = "x-public-location-id";

const rememberedLocationSharedPaths = new Set([
  "/menu",
  "/private-events",
  "/holiday-parties",
  "/catering",
  // Catering ordering shares the same local-preview location memory so a
  // second tenant's scheduled catering flow can be exercised on localhost.
  "/order/catering",
  "/order/catering/checkout",
  "/jobs",
  "/contact",
  "/reservations",
  "/sms-offers",
  "/giveaway",
  "/about",
  "/unsubscribe",
  "/gaming",
  "/welcome",
]);

function cookieValueFromString(cookieString: string, name: string): string | null {
  const encodedName = `${encodeURIComponent(name)}=`;
  const rawCookie = cookieString
    .split(";")
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(encodedName) || cookie.startsWith(`${name}=`));

  if (!rawCookie) return null;

  const value = rawCookie.slice(rawCookie.indexOf("=") + 1);

  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function hostnameFromInput(input: string | null | undefined): string {
  if (!input) return "";

  try {
    const url = input.includes("://")
      ? new URL(input)
      : new URL(`http://${input}`);

    return url.hostname
      .toLowerCase()
      .replace(/^\[([^\]]+)\]$/, "$1");
  } catch {
    const normalized = input.trim().toLowerCase();
    if (normalized === "::1") return normalized;
    return normalized.split(":")[0] ?? "";
  }
}

export function isLocalPreviewPublicHost(input: string | null | undefined): boolean {
  const hostname = hostnameFromInput(input);

  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1";
}

export function resolvePublicLocationCookie(
  value: string | null | undefined,
): PlatformLocation | null {
  if (!value) return null;

  try {
    return getLocationBySlug(value);
  } catch {
    return null;
  }
}

export function readPublicLocationCookie(cookieString: string | null | undefined) {
  if (!cookieString) return null;

  return cookieValueFromString(cookieString, publicLocationCookieName);
}

export function normalizePublicPathname(pathname: string | null | undefined) {
  const normalized = pathname?.split("?")[0]?.split("#")[0]?.replace(/\/+$/, "") ?? "";

  return normalized || "/";
}

export function publicLocationIdFromPathname(pathname: string | null | undefined) {
  return resolveLocationByPathname(pathname ?? undefined)?.id ?? null;
}

export function shouldUseRememberedPublicLocationPath(
  pathname: string | null | undefined,
) {
  const normalizedPathname = normalizePublicPathname(pathname);

  return (
    Boolean(publicLocationIdFromPathname(normalizedPathname)) ||
    rememberedLocationSharedPaths.has(normalizedPathname)
  );
}
